import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import Reveal from "./Reveal";
import { REVIEWS } from "./data";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0a11.99 11.99 0 0 0-1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

const Stars = () => (
  <span className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
    ))}
  </span>
);

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="reviews" data-testid="testimonials-section" className="bg-white py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
              <GoogleG className="h-4 w-4" />
              <span className="text-sm font-semibold text-slate-700">4.9 · 58+ Google Reviews</span>
              <Stars />
            </div>
            <h2 className="mt-4 max-w-xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Siliguri trusts us with its spaces
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="testimonial-carousel-prev-button"
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canPrev}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-all duration-300 hover:border-sapphire hover:text-sapphire disabled:opacity-40"
              aria-label="Previous reviews"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="testimonial-carousel-next-button"
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNext}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-sapphire text-white shadow-lg shadow-sapphire/25 transition-all duration-300 hover:bg-sapphire-dark disabled:opacity-40"
              aria-label="Next reviews"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-12 overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {REVIEWS.map((r) => (
                <article
                  key={r.name}
                  data-testid={`testimonial-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex h-auto min-w-0 flex-[0_0_100%] flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)]"
                >
                  <div className="flex items-center justify-between">
                    <Stars />
                    <GoogleG className="h-4 w-4 opacity-80" />
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">&ldquo;{r.text}&rdquo;</p>
                  <div className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sapphire-light text-sm font-extrabold text-sapphire">
                      {r.name.split(" ").map((w) => w[0]).join("")}
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-slate-900">{r.name}</span>
                      <span className="block text-xs font-medium text-slate-500">{r.area}</span>
                    </span>
                  </div>
                  <span className="mt-3 inline-flex w-fit rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    {r.service}
                  </span>
                </article>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
